import { baseApi } from './baseApi';
import {
  ReceiptDto,
  CreateReceiptDto,
  ReceiptGenerationResultDto,
  ReceiptTemplateDto,
  ReceiptHistoryDto,
  ReceiptHistoryQueryDto,
  PrintJobDto,
  PrintJobResultDto,
  ReceiptShareTokenDto,
  CreateReceiptShareTokenDto,
  ReceiptSequenceDto,
  UpdateReceiptSequenceDto,
  ReceiptVoidDto,
  ReceiptStatsDto,
} from '../types/receipts';

// Create API slice using enhanceEndpoints instead of injectEndpoints
export const receiptsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Receipt generation (renamed from generateReceipt to avoid conflict with ordersApi)
    createReceipt: builder.mutation<ReceiptGenerationResultDto, { restaurantId: string; data: CreateReceiptDto }>({
      query: ({ restaurantId, data }) => ({
        url: `/restaurants/${restaurantId}/receipts/generate`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'Receipt' as const, id: 'LIST' },
        { type: 'ReceiptStats' as const, id: 'PARTIAL' },
      ],
    }),

    // Receipt retrieval
    getReceipt: builder.query<ReceiptDto, { restaurantId: string; receiptId: string }>({
      query: ({ restaurantId, receiptId }) => 
        `/restaurants/${restaurantId}/receipts/${receiptId}`,
      providesTags: (result, error, { receiptId }) => [
        { type: 'Receipt' as const, id: receiptId },
      ],
    }),

    getReceiptPdf: builder.query<Blob, { restaurantId: string; receiptId: string }>({
      query: ({ restaurantId, receiptId }) => ({
        url: `/restaurants/${restaurantId}/receipts/${receiptId}/pdf`,
        responseHandler: (response) => response.blob(),
      }),
      providesTags: (result, error, { receiptId }) => [
        { type: 'Receipt' as const, id: `pdf-${receiptId}` },
      ],
    }),

    // Receipt history
    getReceiptHistory: builder.query<
      ReceiptHistoryDto[],
      { restaurantId: string; query?: ReceiptHistoryQueryDto }
    >({
      query: ({ restaurantId, query = {} }) => ({
        url: `/restaurants/${restaurantId}/receipts/history`,
        params: query,
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Receipt' as const, id: 'LIST' },
        ...(result || []).map(({ receiptId }) => ({ type: 'Receipt' as const, id: receiptId })),
      ],
    }),

    // Receipt templates
    getReceiptTemplates: builder.query<ReceiptTemplateDto[], string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/receipts/templates`,
      providesTags: (result, error, restaurantId) => [
        { type: 'ReceiptTemplate' as const, id: 'LIST' },
      ],
    }),

    getReceiptTemplate: builder.query<ReceiptTemplateDto, { restaurantId: string; templateName: string }>({
      query: ({ restaurantId, templateName }) => 
        `/restaurants/${restaurantId}/receipts/templates/${templateName}`,
      providesTags: (result, error, { templateName }) => [
        { type: 'ReceiptTemplate' as const, id: templateName },
      ],
    }),

    updateReceiptTemplate: builder.mutation<
      ReceiptTemplateDto,
      { restaurantId: string; templateName: string; data: Partial<ReceiptTemplateDto> }
    >({
      query: ({ restaurantId, templateName, data }) => ({
        url: `/restaurants/${restaurantId}/receipts/templates/${templateName}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { templateName }) => [
        { type: 'ReceiptTemplate' as const, id: templateName },
        { type: 'ReceiptTemplate' as const, id: 'LIST' },
      ],
    }),

    // Print jobs (DEPRECATED - renamed to avoid conflict with printersApi)
    printReceiptLegacy: builder.mutation<PrintJobResultDto, { restaurantId: string; data: PrintJobDto }>({
      query: ({ restaurantId, data }) => ({
        url: `/restaurants/${restaurantId}/receipts/print`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'PrintJob' as const, id: 'LIST' },
      ],
    }),

    reprintReceipt: builder.mutation<
      PrintJobResultDto,
      { restaurantId: string; receiptId: string; printerName?: string; copies?: number }
    >({
      query: ({ restaurantId, receiptId, printerName, copies = 1 }) => ({
        url: `/restaurants/${restaurantId}/receipts/${receiptId}/reprint`,
        method: 'POST',
        body: { printerName, copies },
      }),
      invalidatesTags: [
        { type: 'PrintJob' as const, id: 'LIST' },
      ],
    }),

    getPrintJobs: builder.query<PrintJobResultDto[], { restaurantId: string; receiptId?: string }>({
      query: ({ restaurantId, receiptId }) => ({
        url: `/restaurants/${restaurantId}/receipts/print-jobs`,
        params: { receiptId },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'PrintJob' as const, id: 'LIST' },
      ],
    }),

    getPrintJobStatus: builder.query<PrintJobResultDto, { restaurantId: string; jobId: string }>({
      query: ({ restaurantId, jobId }) => 
        `/restaurants/${restaurantId}/receipts/print-jobs/${jobId}`,
      providesTags: (result, error, { jobId }) => [
        { type: 'PrintJob' as const, id: jobId },
      ],
    }),

    // Receipt sharing
    createShareToken: builder.mutation<
      ReceiptShareTokenDto,
      { restaurantId: string; data: CreateReceiptShareTokenDto }
    >({
      query: ({ restaurantId, data }) => ({
        url: `/restaurants/${restaurantId}/receipts/share`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'ReceiptShare' as const, id: 'LIST' },
      ],
    }),

    getSharedReceipt: builder.query<ReceiptDto, { token: string }>({
      query: ({ token }) => `/receipts/shared/${token}`,
      providesTags: (result, error, { token }) => [
        { type: 'ReceiptShare' as const, id: token },
      ],
    }),

    // Email receipts
    emailReceipt: builder.mutation<
      { success: boolean; message: string },
      { restaurantId: string; receiptId: string; emailAddress: string }
    >({
      query: ({ restaurantId, receiptId, emailAddress }) => ({
        url: `/restaurants/${restaurantId}/receipts/${receiptId}/email`,
        method: 'POST',
        body: { emailAddress },
      }),
    }),

    // Receipt sequences
    getReceiptSequences: builder.query<ReceiptSequenceDto[], string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/receipts/sequences`,
      providesTags: (result, error, restaurantId) => [
        { type: 'ReceiptSequence' as const, id: 'LIST' },
      ],
    }),

    getReceiptSequence: builder.query<ReceiptSequenceDto, { restaurantId: string; sequenceId: string }>({
      query: ({ restaurantId, sequenceId }) => 
        `/restaurants/${restaurantId}/receipts/sequences/${sequenceId}`,
      providesTags: (result, error, { sequenceId }) => [
        { type: 'ReceiptSequence' as const, id: sequenceId },
      ],
    }),

    updateReceiptSequence: builder.mutation<
      ReceiptSequenceDto,
      { restaurantId: string; sequenceId: string; data: UpdateReceiptSequenceDto }
    >({
      query: ({ restaurantId, sequenceId, data }) => ({
        url: `/restaurants/${restaurantId}/receipts/sequences/${sequenceId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { sequenceId }) => [
        { type: 'ReceiptSequence' as const, id: sequenceId },
        { type: 'ReceiptSequence' as const, id: 'LIST' },
      ],
    }),

    // Receipt void
    voidReceipt: builder.mutation<
      { success: boolean; message: string },
      { restaurantId: string; receiptId: string; data: ReceiptVoidDto }
    >({
      query: ({ restaurantId, receiptId, data }) => ({
        url: `/restaurants/${restaurantId}/receipts/${receiptId}/void`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { receiptId }) => [
        { type: 'Receipt' as const, id: receiptId },
        { type: 'Receipt' as const, id: 'LIST' },
        { type: 'ReceiptStats' as const, id: 'PARTIAL' },
      ],
    }),

    // Receipt statistics
    getReceiptStats: builder.query<ReceiptStatsDto, string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/receipts/stats`,
      providesTags: (result, error, restaurantId) => [
        { type: 'ReceiptStats' as const, id: restaurantId },
      ],
    }),

    // Receipt settings
    getReceiptSettings: builder.query<any, string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/receipts/settings`,
      providesTags: (result, error, restaurantId) => [
        { type: 'ReceiptSettings' as const, id: restaurantId },
      ],
    }),

    updateReceiptSettings: builder.mutation<any, { restaurantId: string; settings: any }>({
      query: ({ restaurantId, settings }) => ({
        url: `/restaurants/${restaurantId}/receipts/settings`,
        method: 'PUT',
        body: settings,
      }),
      invalidatesTags: (result, error, { restaurantId }) => [
        { type: 'ReceiptSettings' as const, id: restaurantId },
      ],
    }),

    // Bulk operations
    bulkPrintReceipts: builder.mutation<
      { success: boolean; results: PrintJobResultDto[] },
      { restaurantId: string; receiptIds: string[]; printerName?: string }
    >({
      query: ({ restaurantId, receiptIds, printerName }) => ({
        url: `/restaurants/${restaurantId}/receipts/bulk-print`,
        method: 'POST',
        body: { receiptIds, printerName },
      }),
      invalidatesTags: [
        { type: 'PrintJob' as const, id: 'LIST' },
      ],
    }),

    bulkEmailReceipts: builder.mutation<
      { success: boolean; results: any[] },
      { restaurantId: string; receiptIds: string[]; emailAddress: string }
    >({
      query: ({ restaurantId, receiptIds, emailAddress }) => ({
        url: `/restaurants/${restaurantId}/receipts/bulk-email`,
        method: 'POST',
        body: { receiptIds, emailAddress },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateReceiptMutation,
  useGetReceiptQuery,
  useGetReceiptPdfQuery,
  useGetReceiptHistoryQuery,
  usePrintReceiptLegacyMutation, // DEPRECATED - renamed to avoid conflict with printersApi
  useReprintReceiptMutation,
  useGetPrintJobsQuery,
  useGetPrintJobStatusQuery,
  useCreateShareTokenMutation,
  useGetSharedReceiptQuery,
  useEmailReceiptMutation,
  useGetReceiptSequencesQuery,
  useGetReceiptSequenceQuery,
  useUpdateReceiptSequenceMutation,
  useVoidReceiptMutation,
  useGetReceiptStatsQuery,
  useGetReceiptSettingsQuery,
  useUpdateReceiptSettingsMutation,
  useBulkPrintReceiptsMutation,
  useBulkEmailReceiptsMutation,
} = receiptsApi;

// For backward compatibility, export as the old name too
export const useGenerateReceiptMutation = useCreateReceiptMutation;

// DEPRECATED: Use usePrintReceiptMutation from printersApi instead
export const usePrintReceiptMutation = usePrintReceiptLegacyMutation;